import axios from 'axios';
import { supabase } from './supabaseClient';

const BASE_URL = 'https://arndapi.test123.dev';
// const BASE_URL = 'http://localhost:4000';

const api = axios.create({
  baseURL: `${BASE_URL}`,
});

api.interceptors.request.use(
  async (config) => {
    // The supabase client keeps a self-refreshing session (signInWithPassword
    // persists it); always prefer its CURRENT token. The a_access_token
    // snapshot never refreshes and used to log the dashboard out every hour
    // when the 60-minute JWT expired.
    let token = null;
    try {
      const { data } = await supabase.auth.getSession();
      token = data?.session?.access_token ?? null;
    } catch {
      token = null;
    }
    if (!token) token = localStorage.getItem('a_access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// --- Account (SCRUM-271) ---

// The API-side account for the logged-in Supabase user, including `role`
// (PARENT / PHOTOGRAPHER / ADMIN). App.js gates the dashboard on it.
export const getMyAccount = async () => {
  try {
    const response = await api.get('/user/account');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to load account');
  }
};

// --- Legal Documents API ---

export const getDocument = async (type) => {
  try {
    const response = await api.get(`/admin/documents/${type}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to fetch document: ${type}`
    );
  }
};

export const setDocument = async (doc) => {
  try {
    const response = await api.post(`/admin/documents`, doc);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || 'Failed to create/update document'
    );
  }
};

export const updateDocument = async (type, content) => {
  try {
    const response = await api.put(`/admin/documents/${type}`, content);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to update document: ${type}`
    );
  }
};

// --- Prices API ---

// Response also carries `matchThreshold` (89–100, SCRUM-338): auto matches at or
// above it are visible to parents; below it they are held for review on the ops page.
export const getPrices = async () => {
  try {
    const res = await api.get('/admin/prices');
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch prices'
    );
  }
};

export const setPrices = async (payload) => {
  try {
    const res = await api.post('/admin/prices', payload);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || 'Failed to create/update prices'
    );
  }
};

// payload may include `matchThreshold` (89–100); applies immediately to visibility.
export const updatePrices = async (payload) => {
  try {
    const res = await api.put('/admin/prices', payload);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || 'Failed to update prices'
    );
  }
};

// --- Users API ---

export const getUsers = async (filters = {}) => {
  try {
    const res = await api.get('/admin/users', { params: filters });
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch users'
    );
  }
};

export const getUserById = async (userId) => {
  try {
    const res = await api.get(`/admin/users/${userId}`);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to fetch user: ${userId}`
    );
  }
};

export const updateUser = async (userId, data) => {
  try {
    const res = await api.put(`/admin/users/${userId}`, data);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to update user: ${userId}`
    );
  }
};

export const banUser = async (userId) => {
  try {
    const res = await api.put(`/admin/users/${userId}/ban`);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to ban user: ${userId}`
    );
  }
};

export const unbanUser = async (userId) => {
  try {
    const res = await api.put(`/admin/users/${userId}/unban`);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to unban user: ${userId}`
    );
  }
};

// --- Events API ---

export const getAllEvents = async (filters = {}) => {
  try {
    const res = await api.get('/admin/events', { params: filters });
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch events'
    );
  }
};

export const getEventById = async (eventId) => {
  try {
    const res = await api.get(`/admin/events/${eventId}`);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to fetch event: ${eventId}`
    );
  }
};

export const createEvent = async (eventData) => {
  try {
    const res = await api.post('/event', eventData);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || 'Failed to create event'
    );
  }
};

export const updateEvent = async (eventId, eventData) => {
  try {
    const res = await api.put(`/admin/events/${eventId}`, eventData);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to update event: ${eventId}`
    );
  }
};

export const deleteEvent = async (eventId) => {
  try {
    const res = await api.delete(`/admin/events/${eventId}`);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to delete event: ${eventId}`
    );
  }
};

export const assignPhotographers = async (eventId, photographerIds) => {
  try {
    const res = await api.put(`/admin/events/${eventId}/assign-photographers`, { photographerIds });
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to assign photographers to event: ${eventId}`
    );
  }
};

// --- Event operators (SCRUM-453) ---

// { operators: [{ userId, email, fullName }] }
export const getEventOperators = async (eventId) => {
  try {
    const res = await api.get(`/admin/events/${eventId}/operators`);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to fetch operators for event: ${eventId}`
    );
  }
};

// { userId, email, fullName } for a live non-admin account, else the API's
// message ("No ARND account with that email" / "That account is an admin …").
export const lookupOperator = async (email) => {
  try {
    const res = await api.get('/admin/operators/lookup', { params: { email } });
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || 'Failed to look up that email'
    );
  }
};

// Replaces the event's operators with these accounts; returns the new list.
export const setEventOperators = async (eventId, emails) => {
  try {
    const res = await api.put(`/admin/events/${eventId}/operators`, { emails });
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to save operators for event: ${eventId}`
    );
  }
};

// --- Photographers API ---

export const getAllPhotographers = async () => {
  try {
    const res = await api.get('/admin/photographers');
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch photographers'
    );
  }
};

export const updatePhotographer = async (userId, data) => {
  try {
    const res = await api.put(`/admin/photographers/${userId}`, data);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to update photographer: ${userId}`
    );
  }
};

// --- Photos API ---

export const getAllPhotos = async () => {
  try {
    const res = await api.get('/admin/photos');
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch photos'
    );
  }
};

export const getPhotosByUser = async (userId) => {
  try {
    const res = await api.get(`/admin/photos/by-user/${userId}`);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to fetch photos for user: ${userId}`
    );
  }
};

export const getPhotosByEvent = async (eventId) => {
  try {
    const res = await api.get(`/admin/photos/by-event/${eventId}`);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to fetch photos for event: ${eventId}`
    );
  }
};

export const removePhoto = async (photoId) => {
  try {
    const res = await api.delete(`/admin/photos/${photoId}`);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to remove photo: ${photoId}`
    );
  }
};

// --- Statistics API ---

export const getStats = async () => {
  try {
    const res = await api.get('/admin/stats');
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch stats');
  }
};

export const getSalesTrends = async () => {
  try {
    const res = await api.get('/admin/stats/sales-trends');
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch sales trends');
  }
};

export const getUploadsTrends = async () => {
  try {
    const res = await api.get('/admin/stats/uploads-trends');
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch uploads trends');
  }
};

export const getUserActivityTrends = async () => {
  try {
    const res = await api.get('/admin/stats/user-activity');
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch user activity trends');
  }
};

export const getEventAttendanceStats = async () => {
  try {
    const res = await api.get('/admin/stats/event-attendance');
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch event attendance stats');
  }
};

export const getPurchasesStats = async () => {
  try {
    const res = await api.get('/admin/stats/purchases');
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch purchases stats');
  }
};

// --- S3 Image Upload for Events ---

export const getEventImagePresignedUrls = async (iconKey, bannerKey) => {
  try {
    const res = await api.post('/s3/event/icon-banner-urls', {
      iconKey,
      bannerKey,
    });
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to get presigned URLs');
  }
};

export const uploadImageToS3 = async (presignedUrl, file) => {
  try {
    await axios.put(presignedUrl, file, {
      headers: {
        'Content-Type': file.type,
        // 'x-amz-acl': 'public-read',
      },
    });
    return true;
  } catch (error) {
    throw new Error('Failed to upload image to S3');
  }
};

// --- Survey API ---
export const getSurveyQuestions = async () => {
  try {
    const res = await api.get('/survey/questions');
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch survey questions');
  }
};

export const setSurvey = async (questions) => {
  try {
    const res = await api.put('/admin/survey', questions);
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to set survey questions');
  }
};

// --- Photo Match API ---
export const runPhotoMatch = async ({ eventId, photoId }) => {
  try {
    const res = await api.post('/admin/photos/match', { eventId, photoId });
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to run photo match');
  }
};

// SCRUM-393 test-event purge, SCRUM-417 moved off the E2E harness (removed):
// same eventType === "test" gate as the old E2E cleanup route.
export const purgeTestEvent = async (eventId) => {
  try {
    const res = await api.post(`/admin/events/${eventId}/purge-test`);
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || `Failed to purge test event: ${eventId}`);
  }
};

// --- Event Ops Console (SCRUM-327) ---

export const getEventOps = async (eventId, attendeeId) => {
  try {
    const res = await api.get(`/admin/events/${eventId}/ops`, { params: attendeeId ? { attendeeId } : {} });
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch event ops summary');
  }
};

// SCRUM-451: just the count cards (optionally for one athlete) — an athlete
// click refreshes these without the event-wide roster/pipeline.
export const getEventOpsCounts = async (eventId, attendeeId) => {
  try {
    const res = await api.get(`/admin/events/${eventId}/ops/counts`, { params: attendeeId ? { attendeeId } : {} });
    return res.data.counts;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch event ops counts');
  }
};

// view: "all" | "unmatched" | "attendee" | "deleted" (SCRUM-451: the old per-status views
//   — matched/assigned/review/suppressed/incart/purchased — are now filters within "all").
// statuses: comma list (or repeated param) of "matched" | "assigned" (manual + inferred
//   matches) | "review" (SCRUM-357: held for a human — low score, adult face, small face,
//   eyes closed) | "suppressed" (matches an operator hid) — an OR within statuses, combines
//   only with view "all".
// tags: comma list of "incart" | "purchased" — an OR within tags, AND'd with any statuses,
//   combines only with view "all".
// sort: "upload" (FTP arrival, default) | "runs" (a photographer's burst stays together)
//   | "gallery" (the app's own order — camera serial, capture time, upload time — always
//   ascending; each photo then carries `pos`, the number the parent reads in the app viewer,
//   and `bought`; bought photos are numbered within the Purchased tab, the rest within
//   Available, over the whole set, not the page — SCRUM-423).
// scope: with view "attendee", "parent" = every live athlete RSVP'd under the same parent
//   (the set the app shows) instead of just that athlete (SCRUM-423). Response echoes `scope`.
// camera: exact cameraModel to restrict to. Response adds `sort`, `cameras: [{model, photos}]`
// and per photo `takenAt`, `cameraModel`, `originalFilename` (SCRUM-334).
// Each photo's `matchedAttendees[]` carries `{ attendeeId, fullName, confidenceScore,
// status: "auto"|"confirmed"|"suppressed", source: "recognition"|"manual"|"inferred",
// visible, manual, inferred, commerce }` (SCRUM-338), plus `reviewReasons: string[]`
// (SCRUM-357: stored reasons — "adultFace"|"smallFace"|"eyesClosed" — always an array,
// never null; "low score" is not stored, see viewerKeys.reviewReasons()). `faces[]`
// carries the same `reviewReasons` per face.
export const getEventOpsPhotos = async (eventId, { view, statuses, tags, attendeeId, page, pageSize, order, sort, scope, camera } = {}) => {
  try {
    const res = await api.get(`/admin/events/${eventId}/ops/photos`, {
      params: { view, statuses, tags, attendeeId, page, pageSize, order, sort, scope, camera },
    });
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch event photos');
  }
};

// SCRUM-386: `faceId` (Rekognition id from the ops `faces[]` payload) makes the
// manual match carry that face; omit it for a face-less tag.
export const addManualMatch = async (photoId, attendeeId, faceId = null) => {
  try {
    const res = await api.post(`/admin/photos/${photoId}/matches`, {
      attendeeId,
      ...(faceId ? { faceId } : {}),
    });
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to add manual match');
  }
};

// SCRUM-338: DELETE no longer removes the row — it marks the match `suppressed`
// (hidden from the parent, restorable from the Suppressed view).
export const suppressMatch = async (photoId, attendeeId) => {
  try {
    const res = await api.delete(`/admin/photos/${photoId}/matches/${attendeeId}`);
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to suppress match');
  }
};

// Promote a review match (auto match below the threshold, or held for another reason) to a confirmed, visible match.
export const confirmMatch = async (photoId, attendeeId) => {
  try {
    const res = await api.post(`/admin/photos/${photoId}/matches/${attendeeId}/confirm`);
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to confirm match');
  }
};

// Undo a suppression; the match returns to its pre-suppression visibility.
export const restoreMatch = async (photoId, attendeeId) => {
  try {
    const res = await api.post(`/admin/photos/${photoId}/matches/${attendeeId}/restore`);
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to restore match');
  }
};

export const downloadAttendeeZip = async (eventId, attendeeId, fileName) => {
  try {
    const res = await api.get(
      `/admin/events/${eventId}/ops/attendees/${attendeeId}/download`,
      { responseType: 'blob' }
    );
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'photos.zip';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    throw new Error('Failed to download photo bundle');
  }
};

export const rematchAttendee = async (eventId, attendeeId) => {
  try {
    const res = await api.post(`/admin/events/${eventId}/ops/attendees/${attendeeId}/rematch`);
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to re-run matching');
  }
};

export const restorePhoto = async (photoId) => {
  try {
    const res = await api.put(`/admin/photos/${photoId}/restore`);
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to restore photo');
  }
};

export const getEventOpsFunnel = async (eventId) => {
  try {
    const response = await api.get(`/admin/events/${eventId}/ops/funnel`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch event funnel');
  }
};

// --- Campaign send (SCRUM-356) ---

// filters: { stages: string[], channels: string[], smsFallback?, signedUpAfter?, signedUpBefore?, excludeParentIds?: string[] }
export const getCampaignAudience = async (eventId, filters = {}) => {
  try {
    const { excludeParentIds, stages, channels, ...rest } = filters;
    const params = {
      ...rest,
      stages: stages?.length ? stages.join(',') : undefined,
      channels: channels?.length ? channels.join(',') : undefined,
      excludeParentIds: excludeParentIds?.length ? excludeParentIds.join(',') : undefined,
    };
    const res = await api.get(`/admin/events/${eventId}/campaign/audience`, { params });
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch campaign audience');
  }
};

// payload: { filters: { stages, channels, smsFallback?, excludeParentIds?, signedUpAfter?, signedUpBefore? },
//            title?, subject?, body, bodies?, dryRun? }
// dryRun → the SendReport (with `sample`); otherwise { runId } — poll getCampaignRun.
export const sendCampaign = async (eventId, payload) => {
  try {
    const res = await api.post(`/admin/events/${eventId}/campaign/send`, payload);
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to send campaign');
  }
};

export const checkCampaignDevices = async (eventId) => {
  try {
    const res = await api.post(`/admin/events/${eventId}/campaign/check-devices`);
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to check devices');
  }
};

// --- Campaign runs, contact history, per-event mute (SCRUM-449) ---

// { run: { id, eventId, kind, label, status: 'running'|'done'|'failed'|'missed', reason, counts|null,
//          createdAt, updatedAt, finishedAt }, rows: [{ id, parentId, parentName, contact, status, … }] }
// A 'running' run whose updatedAt is over 2 min old has stopped.
export const getCampaignRun = async (eventId, runId) => {
  try {
    const res = await api.get(`/admin/events/${eventId}/campaign/runs/${runId}`);
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch campaign run');
  }
};

// → { runId }; 409 while the run is still in progress.
export const resumeCampaignRun = async (eventId, runId) => {
  try {
    const res = await api.post(`/admin/events/${eventId}/campaign/runs/${runId}/resume`);
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to resume campaign run');
  }
};

// → [{ runId, label, kind, channel, status, at }], newest first.
export const getParentCampaignHistory = async (eventId, parentId) => {
  try {
    const res = await api.get(`/admin/events/${eventId}/campaign/parents/${parentId}/history`);
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch contact history');
  }
};

export const setParentMute = async (eventId, parentId, muted) => {
  try {
    const res = await api.post(`/admin/events/${eventId}/campaign/parents/${parentId}/mute`, { muted });
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to change mute');
  }
};

// --- Automated campaigns: event timeline (SCRUM-449) ---
// Occurrence ids contain ':' — always encodeURIComponent them.

export const getCampaignTimeline = async (eventId) => {
  try {
    const res = await api.get(`/admin/events/${eventId}/campaigns`);
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch campaign timeline');
  }
};

export const skipOccurrence = async (eventId, occId, skipped) => {
  try {
    const res = await api.post(
      `/admin/events/${eventId}/campaigns/${encodeURIComponent(occId)}/skip`,
      { skipped }
    );
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to skip campaign');
  }
};

// → { runId }
export const sendOccurrenceNow = async (eventId, occId) => {
  try {
    const res = await api.post(`/admin/events/${eventId}/campaigns/${encodeURIComponent(occId)}/send-now`);
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to send campaign now');
  }
};

// content: { title?, subject?, bodies } — null resets to the playbook's.
export const overrideOccurrence = async (eventId, occId, content) => {
  try {
    const res = await api.put(
      `/admin/events/${eventId}/campaigns/${encodeURIComponent(occId)}/override`,
      { content }
    );
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to save campaign override');
  }
};

// --- Automated campaigns: playbook (SCRUM-449) ---

export const getPlaybook = async () => {
  try {
    const res = await api.get('/admin/campaign-playbook');
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch campaign playbook');
  }
};

// { dryRun: true } → { impact } of adding it, without saving.
export const createPlaybookCampaign = async (campaign, { dryRun = false } = {}) => {
  try {
    const res = await api.post('/admin/campaign-playbook', campaign, {
      params: dryRun ? { dryRun: true } : {},
    });
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to create campaign');
  }
};

// { dryRun: true } → { impact } without saving.
export const updatePlaybookCampaign = async (id, campaign, { dryRun = false } = {}) => {
  try {
    const res = await api.put(`/admin/campaign-playbook/${id}`, campaign, {
      params: dryRun ? { dryRun: true } : {},
    });
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to update campaign');
  }
};

export const deletePlaybookCampaign = async (id) => {
  try {
    const res = await api.delete(`/admin/campaign-playbook/${id}`);
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to delete campaign');
  }
};

export const reorderPlaybook = async (ids) => {
  try {
    const res = await api.put('/admin/campaign-playbook/order', { ids });
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to reorder campaigns');
  }
};

// → { sample: [{ parent, label, channel, body }] }, rendered against the event's real parents.
export const previewPlaybookCampaign = async (eventId, campaign) => {
  try {
    const res = await api.post('/admin/campaign-playbook/preview', { eventId, campaign });
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to preview campaign');
  }
};

// Used by the purchase strip (SCRUM-356) and, before that, Task 11's feed panel.
export const getEventFeed = async (eventId, { since, limit, kind } = {}) => {
  try {
    const res = await api.get(`/admin/events/${eventId}/feed`, {
      params: { since, limit, kind },
    });
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch event feed');
  }
};

// --- Browser photo upload (SCRUM-415) ---

// Names of every live photo in the event (= Photos.originalFilename), newest first.
export const getUploadedPhotos = async (eventId) => {
  try {
    const res = await api.get(`/admin/events/${eventId}/photos/uploaded`);
    return res.data; // { count, files: [{ name, at }] }
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to list uploaded photos');
  }
};

// One file → the event's FTP folder as <prefix>-<name>. Resolves to the per-file
// result { name, original, bytes, status: 'pushed'|'duplicate'|'failed', error? }.
export const uploadEventPhoto = async (eventId, file, prefix, { onProgress, signal } = {}) => {
  const form = new FormData();
  if (prefix) form.append('prefix', prefix);
  form.append('images', file, file.name);
  try {
    const res = await api.post(`/admin/events/${eventId}/photos/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 10 * 60 * 1000,
      signal,
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(e.loaded / e.total);
      },
    });
    return res.data.results[0];
  } catch (error) {
    if (axios.isCancel(error)) throw error;
    const err = new Error(error.response?.data?.message || error.message || 'Upload failed');
    // No HTTP response at all = the connection dropped (or the request timed
    // out): the uploader waits and retries these without limit.
    err.network = !error.response;
    throw err;
  }
};
